import Media from 'react-media';

import {
  Button,
  Grid,
  Text,
  Box,
  useTheme,
  Badge,
} from '@simplywallst/ui-core';
import PlusSolid from '@simplywallst/ui-core/icons/PlusSolid';
import { useUserAgent } from '@simplywallst/hooks/useUserAgent';
import { Sentry } from '@/components/Sentry';
import { useTypeformDrawer } from '@/hooks/useTypeformDrawer';
import loadable from '@loadable/component';
import { useCompanyNarratives } from '@/pages/CompanyReport/components/CompanyNarratives/hooks/useCompanyNarratives';
import { Section } from '@/pages/CompanyReport/components/Overview/components/Section';
import { SubSectionHeader } from '@/pages/CompanyReport/components/Overview/components/SubSectionHeader';

import { StyledHeaderGrid } from './styled';

const CompanyNarrative = loadable(
  () => import('./components/CompanyNarrative')
);

const CompanyNarratives = ({ canonicalUrl }: { canonicalUrl: string }) => {
  const { data: narratives, status: narrativesStatus } =
    useCompanyNarratives(canonicalUrl);
  const { isMobile, isDesktop } = useUserAgent();
  const theme = useTheme();
  const makeTypeformPopup = useTypeformDrawer('suwuMcHR');

  if (narrativesStatus === 'error' || narrativesStatus === 'pending') {
    return null;
  }

  if (typeof narratives === 'undefined' || narratives.length === 0) {
    return null;
  }

  return (
    <Section data-cy-id="company-narratives">
      <StyledHeaderGrid
        as="header"
        templateColumns="auto auto"
        gap="8px"
        justifyContent="space-between"
        alignItems="center"
      >
        <Grid
          templateColumns="auto auto"
          gap="8px"
          justifyContent="start"
          alignItems="center"
        >
          <SubSectionHeader data-cy-id="company-narratives-heading">
            Narratives
          </SubSectionHeader>

          <Badge tone="attention">Beta</Badge>
        </Grid>
        <Button
          data-cy-id="create-narrative"
          styleType="loud"
          display="inline"
          onClick={makeTypeformPopup}
          alignIcon="left"
        >
          <PlusSolid />
          Create narrative
        </Button>
      </StyledHeaderGrid>
      <Box marginBottom="x2">
        <Text typography="xSmall" data-cy-id="company-narratives-by-line">
          Narratives bring a range of perspectives from our community.
        </Text>
      </Box>
      <Media
        queries={{
          mobile: { maxWidth: theme.xsMaxRaw },
          desktop: { minWidth: theme.lgMinRaw },
        }}
        defaultMatches={{
          mobile: isMobile,
          desktop: isDesktop,
        }}
      >
        {(matches) => {
          return (
            <Sentry
              sentryCapture={{ team: 'narwhals', extra: { canonicalUrl } }}
              globalDisabled
            >
              <Grid
                gap="8px"
                templateColumns={
                  matches.desktop
                    ? `repeat(${narratives.length >= 2 ? 2 : 1}, 1fr)`
                    : undefined
                }
              >
                {narratives
                  .slice(0, narratives.length >= 2 ? 2 : 1)
                  .map((narrative) => (
                    <CompanyNarrative
                      narrative={narrative}
                      key={narrative.id}
                    />
                  ))}
              </Grid>
            </Sentry>
          );
        }}
      </Media>
    </Section>
  );
};

export default CompanyNarratives;
